/**
 * Every gameplay number for Georgi lives here: speeds, physics, timers, transition durations, loop
 * rates and the collision body. Pure TypeScript (no Phaser). `validateTuning` is unit-tested and
 * runs at boot, so a bad edit fails loudly instead of "feeling odd".
 *
 * Units are world px and px/s unless stated. World px = master px × `worldScale`: the N1 master
 * page is 640 px and the keys@1x tier (320 px page) is drawn 1:1 at worldScale 0.5, so Georgi
 * stands ~122 px tall in a 1280×720 canvas. Switching tiers never changes world geometry — the
 * sprite is scaled by worldScale / tierScale.
 */
import { N1_ACTIONS, type N1ActionId } from '../assets/n1Manifest';

export const LOOP_ACTIONS = ['idle-loop', 'walk-loop', 'run-loop'] as const;
export type LoopActionId = (typeof LOOP_ACTIONS)[number];
export type TransitionActionId = Exclude<N1ActionId, LoopActionId>;

export const TRANSITION_ACTIONS = N1_ACTIONS.filter(
  (id): id is TransitionActionId => !(LOOP_ACTIONS as readonly string[]).includes(id),
);

/** Rectangle in master px (640 page) relative to the root/pivot; +y is down, so `y` is negative. */
export interface BodyRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface PlayerTuning {
  /** Master px → world px. 0.5 draws the @1x tiers 1:1. */
  worldScale: number;
  /** px/s */
  walkSpeed: number;
  runSpeed: number;
  /** px/s² toward the input speed while grounded. */
  groundAcceleration: number;
  /** px/s² toward 0 while braking (no input) and during the first half of a turn. */
  brakeDeceleration: number;
  /** px/s² of air control toward the held direction (momentum is kept when nothing is held). */
  airAcceleration: number;
  /** px/s² downward (also fed to Arcade's world gravity). */
  gravity: number;
  /** px/s terminal fall speed. */
  maxFallSpeed: number;
  /** px/s upward at takeoff. Apex = jumpVelocity² / (2·gravity). */
  jumpVelocity: number;
  /** A jump press this long after walking off a ledge still takes off (never after a real jump). */
  coyoteTimeMs: number;
  /** A jump press this long before landing fires on the landing tick. */
  jumpBufferMs: number;
  /**
   * Phase-driven transition clips: tuned wall-clock durations (ms) — never the 1.6–2.4 s review
   * timings. The two descent clips are driven by the fall phase while airborne; their entry here
   * paces the part of the clip left to play once Georgi lands early (on a platform above takeoff).
   * run-to-jump is driven by the ascent phase; its entry is only a nominal value for the overlay.
   */
  transitionMs: Record<TransitionActionId, number>;
  /**
   * Loop cycles in ms. Walk and run are stride-synced: their phase advances with the distance
   * travelled (`loopStridePx` per cycle), so the feet stay planted while accelerating or braking;
   * at the nominal speed that equals wall-clock at this cycle. The run cycle is a whole number of
   * 60 Hz frames (the rebuilt 36-frame run cycle × 16.67 ms = 600 ms) so full speed shows exactly
   * one new frame per render frame.
   */
  loopCycleMs: Record<LoopActionId, number>;
  /** World px travelled per loop cycle (walk/run): stride = speed × cycle. */
  loopStridePx: Record<'walk-loop' | 'run-loop', number>;
  /** Stride sync rate limits: the loop never freezes (min) nor spins (max) off-speed. */
  loopRate: { min: number; max: number };
  /**
   * Runtime in-betweening: draw the next frame over the current one at the sub-frame fraction.
   * OFF since every action is rebuilt from its authored keys with real in-betweens: a cross-dissolve
   * of two crisp frames whose paws sit 5–10 px apart reads as a double image, which is exactly what
   * the rebuild removed. `?blend=1` turns it back on for comparison.
   */
  frameBlend: boolean;
  /** Fraction of a turn spent bleeding off speed before stepping out in the new direction. */
  turnPivot: number;
  /**
   * A transition interrupted before this phase is visually still its from-state (its pose is
   * nearest the from-contact), after it the to-state. Decides which clip chain an interruption
   * continues with, so a tap-release-tap does not replay a full brake and restart.
   */
  clipMidpoint: number;
  /**
   * Jumps from idle or walk have no authored takeoff (N1 gap): they enter run-to-jump at its
   * first frame whose rootLift is at least this many master px, accepting the pose pop.
   */
  jumpEntryLiftPx: number;
  /**
   * Collision bodies in master px relative to the root, one per locomotion family; every bottom
   * sits on the feet line and every rect is centred on the root (the body never mirrors).
   * Authored from the measured alpha bounds (see tests/unit/frameBounds.spec.ts), never the page.
   */
  bodies: { ground: BodyRect; run: BodyRect; air: BodyRect };
  /** Feet line in master px relative to the root (0 = the pivot line, where every N1 action's feet sit). */
  feetLineY: number;
}

export const PLAYER_TUNING: PlayerTuning = {
  worldScale: 0.5,
  walkSpeed: 180,
  runSpeed: 420,
  groundAcceleration: 1800,
  brakeDeceleration: 1500,
  airAcceleration: 900,
  gravity: 2200,
  maxFallSpeed: 1400,
  jumpVelocity: 760,
  coyoteTimeMs: 100,
  jumpBufferMs: 120,
  transitionMs: {
    'idle-to-walk': 200,
    'walk-to-run': 250,
    'run-to-walk': 250,
    'run-to-brake-to-idle': 350,
    'walk-to-brake-to-idle': 250,
    'walk-turn': 300,
    'run-turn': 350,
    'run-to-jump': 300,
    'jump-to-run': 300,
    'jump-to-idle': 350,
  },
  loopCycleMs: {
    'idle-loop': 1600,
    'walk-loop': 720,
    'run-loop': 600,
  },
  loopStridePx: {
    'walk-loop': 130,
    'run-loop': 252,
  },
  loopRate: { min: 0.35, max: 1.6 },
  frameBlend: false,
  turnPivot: 0.5,
  clipMidpoint: 0.5,
  jumpEntryLiftPx: 1,
  // From the median alpha bounds of each family's frames on the smooth tier (master px = 2× tier
  // px): idle/walk ≈ 148 wide × 126 tall, run ≈ 159 × 111 and lower, airborne ≈ 150 × 105 tucked.
  // Bodies keep ~60–65 % of the width (head, tail and reaching paws stay outside so grazes are
  // forgiven) and ~80 % of the height, closed on the feet line and centred on the root.
  bodies: {
    ground: { x: -92, y: -200, w: 184, h: 200 },
    run: { x: -104, y: -172, w: 208, h: 172 },
    air: { x: -88, y: -160, w: 176, h: 160 },
  },
  feetLineY: 0,
};

/** Jump apex in world px for a tuning. */
export function jumpApexPx(t: PlayerTuning): number {
  return (t.jumpVelocity * t.jumpVelocity) / (2 * t.gravity);
}

/** Ascent (= descent on flat ground) time in ms. */
export function jumpAscentMs(t: PlayerTuning): number {
  return (t.jumpVelocity / t.gravity) * 1000;
}

/** Horizontal distance (px) a flat jump covers at a constant ground speed. */
export function jumpRangePx(t: PlayerTuning, speedPxPerS: number): number {
  return speedPxPerS * 2 * (t.jumpVelocity / t.gravity);
}

/**
 * Height (px above the takeoff line) the feet are at after covering `dx` px horizontally at
 * `speed`, for a jump at the tuned velocity (0 once the arc has come back down).
 */
export function jumpHeightAtDistance(t: PlayerTuning, speedPxPerS: number, dx: number): number {
  if (speedPxPerS <= 0) return 0;
  const time = dx / speedPxPerS;
  return Math.max(0, t.jumpVelocity * time - 0.5 * t.gravity * time * time);
}

/** Apex of a launch (spring) at `velocity` px/s under the tuned gravity. */
export function launchApexPx(t: PlayerTuning, velocity: number): number {
  return (velocity * velocity) / (2 * t.gravity);
}

export const TRANSITION_MS_MIN = 100;
export const TRANSITION_MS_MAX = 600;

/** Every problem with a tuning (empty = valid). Pure, so it is unit-tested and also run at boot. */
export function validateTuning(t: PlayerTuning): string[] {
  const problems: string[] = [];
  const positive = (name: string, v: number) => {
    if (!Number.isFinite(v) || v <= 0) problems.push(`${name} must be > 0 (got ${v})`);
  };
  const nonNegative = (name: string, v: number) => {
    if (!Number.isFinite(v) || v < 0) problems.push(`${name} must be >= 0 (got ${v})`);
  };
  positive('worldScale', t.worldScale);
  positive('walkSpeed', t.walkSpeed);
  positive('runSpeed', t.runSpeed);
  if (t.runSpeed <= t.walkSpeed) problems.push('runSpeed must exceed walkSpeed');
  positive('groundAcceleration', t.groundAcceleration);
  positive('brakeDeceleration', t.brakeDeceleration);
  positive('airAcceleration', t.airAcceleration);
  positive('gravity', t.gravity);
  positive('maxFallSpeed', t.maxFallSpeed);
  positive('jumpVelocity', t.jumpVelocity);
  if (t.jumpVelocity > t.maxFallSpeed) problems.push('jumpVelocity must not exceed maxFallSpeed');
  nonNegative('coyoteTimeMs', t.coyoteTimeMs);
  nonNegative('jumpBufferMs', t.jumpBufferMs);
  for (const id of TRANSITION_ACTIONS) {
    const ms = t.transitionMs[id];
    if (ms === undefined) problems.push(`transitionMs.${id} missing`);
    else if (!Number.isFinite(ms) || ms < TRANSITION_MS_MIN || ms > TRANSITION_MS_MAX)
      problems.push(
        `transitionMs.${id} must be within ${TRANSITION_MS_MIN}–${TRANSITION_MS_MAX} ms (got ${ms})`,
      );
  }
  for (const id of Object.keys(t.transitionMs))
    if (!(TRANSITION_ACTIONS as readonly string[]).includes(id))
      problems.push(`transitionMs.${id} is not a transition action`);
  for (const id of LOOP_ACTIONS) {
    const ms = t.loopCycleMs[id];
    if (ms === undefined) problems.push(`loopCycleMs.${id} missing`);
    else positive(`loopCycleMs.${id}`, ms);
  }
  for (const id of Object.keys(t.loopCycleMs))
    if (!(LOOP_ACTIONS as readonly string[]).includes(id))
      problems.push(`loopCycleMs.${id} is not a loop action`);
  for (const id of ['walk-loop', 'run-loop'] as const) {
    const stride = t.loopStridePx[id];
    if (stride === undefined) problems.push(`loopStridePx.${id} missing`);
    else {
      positive(`loopStridePx.${id}`, stride);
      const speed = id === 'run-loop' ? t.runSpeed : t.walkSpeed;
      const expected = (speed * t.loopCycleMs[id]) / 1000;
      if (Math.abs(stride - expected) > 0.05 * stride)
        problems.push(
          `loopStridePx.${id} (${stride}) must equal speed × cycle (${expected.toFixed(0)}) within 5 %`,
        );
    }
  }
  if (!(t.loopRate.min > 0 && t.loopRate.min < 1 && t.loopRate.max >= 1))
    problems.push('loopRate must satisfy 0 < min < 1 <= max');
  if (!(t.turnPivot > 0 && t.turnPivot < 1)) problems.push('turnPivot must be within (0, 1)');
  if (!(t.clipMidpoint > 0 && t.clipMidpoint < 1))
    problems.push('clipMidpoint must be within (0, 1)');
  nonNegative('jumpEntryLiftPx', t.jumpEntryLiftPx);
  for (const [name, body] of Object.entries(t.bodies) as [string, BodyRect][]) {
    positive(`bodies.${name}.w`, body.w);
    positive(`bodies.${name}.h`, body.h);
    if (body.y + body.h !== t.feetLineY)
      problems.push(
        `bodies.${name} bottom (${body.y + body.h}) must sit on the feet line (${t.feetLineY}) — feet on the pivot`,
      );
    if (Math.abs(body.x + body.w / 2) > 0.5)
      problems.push(
        `bodies.${name} must be centred on the root x (it is not mirrored with the sprite)`,
      );
  }
  for (const name of ['ground', 'run', 'air'] as const)
    if (!t.bodies[name]) problems.push(`bodies.${name} missing`);
  return problems;
}
